import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import {
  AnalysisResult,
  FollowersData,
  FollowingData,
  HistoricalAnalysis,
  ComparisonResult,
} from '@/types/instagram';
import { computeComparison } from '@/utils/comparison';
import { toast } from 'sonner';
import { useLanguage } from '@/contexts/LanguageContext';
import { backendApi } from '@/lib/backendApi';

const assertAnalysisContract = (analyses: unknown[]) => {
  if (!import.meta.env.DEV || analyses.length === 0) return;
  const first = analyses[0];
  if (!first || typeof first !== 'object') {
    throw new Error('[instagram-analyses contract] analyses must be an array of objects');
  }

  const required = [
    'id',
    'created_at',
    'followers_count',
    'following_count',
    'not_following_you_back',
    'not_following_back'
  ];
  const missing = required.filter((key) => !(key in (first as Record<string, unknown>)));
  if (missing.length > 0) {
    throw new Error(
      `[instagram-analyses contract] analysis payload is missing fields: ${missing.join(', ')}`
    );
  }
};

export const useInstagramAnalysis = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const tRef = useRef(t);
  const [historicalAnalyses, setHistoricalAnalyses] = useState<HistoricalAnalysis[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);

  useEffect(() => {
    tRef.current = t;
  }, [t]);

  const loadHistoricalAnalyses = useCallback(async () => {
    if (!user) {
      setIsLoadingHistory(false);
      return;
    }

    setIsLoadingHistory(true);
    try {
      const { analyses } = await backendApi.listInstagramAnalyses();
      assertAnalysisContract((analyses || []) as unknown[]);
      setHistoricalAnalyses((analyses || []) as HistoricalAnalysis[]);
    } catch (error) {
      console.error('Error loading historical analyses:', error);
      toast.error(tRef.current('common.errorLoadingHistory'));
    } finally {
      setIsLoadingHistory(false);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      void loadHistoricalAnalyses();
      return;
    }

    setIsLoadingHistory(false);
    setHistoricalAnalyses([]);
  }, [user, loadHistoricalAnalyses]);

  const saveAnalysis = async (
    result: AnalysisResult,
    followersData: FollowersData,
    followingData: FollowingData
  ) => {
    if (!user) {
      toast.error(t('common.errorLoginRequired'));
      return null;
    }

    setIsLoading(true);
    const extraInsights = result.extraInsights ?? {
      pendingFollowRequests: [],
      followRequestsReceived: [],
      favoriteProfiles: [],
      blockedProfiles: [],
      closeFriends: [],
      hiddenStories: []
    };

    try {
      const { analysis } = await backendApi.saveInstagramAnalysis({
        followersCount: result.followers,
        followingCount: result.following,
        followersData,
        followingData,
        notFollowingYouBack: result.notFollowingYouBack,
        notFollowingBack: result.notFollowingBack,
        pendingFollowRequests: extraInsights.pendingFollowRequests,
        followRequestsReceived: extraInsights.followRequestsReceived,
        favoriteProfiles: extraInsights.favoriteProfiles,
        blockedProfiles: extraInsights.blockedProfiles,
        closeFriends: extraInsights.closeFriends,
        hiddenStories: extraInsights.hiddenStories
      });

      const savedAnalysis = analysis as HistoricalAnalysis;
      setHistoricalAnalyses((prev) => [savedAnalysis, ...prev]);
      return savedAnalysis;
    } catch (error) {
      console.error('Error saving analysis:', error);
      toast.error(t('common.errorSavingAnalysis'));
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  const compareWithPrevious = (
    currentResult: AnalysisResult,
    currentFollowersData?: FollowersData | { relationships_followers: FollowersData },
    currentFollowingData?: FollowingData
  ): ComparisonResult | null => {
    return computeComparison(
      historicalAnalyses,
      currentResult,
      currentFollowersData,
      currentFollowingData
    );
  };

  const deleteAnalysis = async (analysisId: string) => {
    if (!user) return;

    try {
      await backendApi.deleteInstagramAnalysis(analysisId);

      setHistoricalAnalyses((prev) => prev.filter((analysis) => analysis.id !== analysisId));
    } catch (error) {
      console.error('Error deleting analysis:', error);
      toast.error(t('common.errorDeletingAnalysis'));
    }
  };

  return {
    historicalAnalyses,
    isLoading,
    isLoadingHistory,
    saveAnalysis,
    compareWithPrevious,
    deleteAnalysis,
    loadHistoricalAnalyses
  };
};
